import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import type { z } from "zod";

import LabeledField from "@/components/form/labeled-field";
import type { InputProps } from "@/components/ui/input";
import { Input } from "@/components/ui/input";
import type { FieldBaseProps } from "@/types/form";
import { getOnChangeFromZod, handleMinMax } from "@/utils/form";

export type FieldInputProps<
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
> = FieldBaseProps<TFormData, TName, TSchema, TData> & InputProps;

const FieldInput = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  onChange,
  validators,
  labelProps,
  gridRow,
  ...props
}: FieldInputProps<TFormData, TName, TSchema, TData>) => {
  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => {
        return (
          <LabeledField
            label={label}
            fieldName={field.name.toString()}
            errors={field.state.meta.errors}
            required={props.required}
            gridRow={gridRow}
            {...labelProps}
          >
            <Input
              value={(field.state.value as string | null)?.toString() ?? ""}
              onBlur={() => {
                if (props.type === "number") {
                  (
                    field.handleChange as (
                      updater: Updater<string | number | null>
                    ) => void
                  )(
                    handleMinMax(
                      field.state.value as number | null,
                      props.min,
                      props.max
                    )
                  );
                }
                field.handleBlur();
              }}
              onChange={(e) => {
                if (onChange) onChange(e);
                const value = e.target.value;
                (
                  field.handleChange as (
                    updater: Updater<string | number | null>
                  ) => void
                )(
                  props.type === "number"
                    ? value !== ""
                      ? handleMinMax(value, props.min, props.max)
                      : null
                    : value
                );
              }}
              errors={!!field.state.meta.errors?.length}
              {...props}
            />
          </LabeledField>
        );
      }}
    />
  );
};

export default FieldInput;
